import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { DashboardHeader } from "@/components/DashboardHeader";
import { ClinicalPageShell } from "@/components/clinical";
import { PageLoader } from "@/components/PageLoader";
import { Button } from "@/components/ui/button";
import { SettingsSectionBack } from "@/components/settings/SettingsSectionHub";
import { CrudEditorShell } from "@/components/workspace/CrudEditorShell";
import { FieldLabel } from "@/components/help/FieldHelpTip";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, MapPin, Pencil, Phone, Plus, Star, Trash2, Clock } from "lucide-react";
import {
  LocationOperationalWindowsForm,
  defaultLocationOpsForm,
  locationOpsFromApi,
  type LocationOpsFormState,
} from "@/components/locations/LocationOperationalWindowsForm";
import { apiGetJson, apiSendJson, apiDelete } from "@/lib/sm-api";
import { QuotaLimitModal, type QuotaLimitPayload } from "@/components/QuotaLimitModal";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { useTenantContext } from "@/lib/tenant-context";
import { branchTypesForBusinessProfile } from "@/lib/business-profile";
import { toast } from "sonner";
import {
  NormalizedAddressForm,
} from "@/components/address/NormalizedAddressForm";
import {
  EMPTY_NORMALIZED_ADDRESS,
  formatNormalizedAddressLine,
  normalizeArPostalCode,
  validateNormalizedAddress,
  type NormalizedAddress,
} from "@structura/core/address";

export const Route = createFileRoute("/app/sucursales")({
  component: SucursalesPage,
  head: () => ({ meta: [{ title: "Sucursales — Structura Market" }] }),
});

export type BranchType = "store" | "pickup" | "dark_store" | "dark_kitchen" | "warehouse" | "distribution_center";

export const BRANCH_TYPE_LABELS: Record<BranchType, string> = {
  store: "Local de venta",
  pickup: "Punto de retiro",
  dark_store: "Dark Store",
  dark_kitchen: "Dark Kitchen",
  warehouse: "Depósito",
  distribution_center: "Centro de distribución",
};

export const BRANCH_TYPE_DESCRIPTIONS: Record<BranchType, string> = {
  store: "Local con atención al público y venta directa.",
  pickup: "Punto de retiro sin atención permanente.",
  dark_store: "Almacén interno para pedidos de delivery.",
  dark_kitchen: "Cocina ghost sin salón ni atención al público.",
  warehouse: "Depósito general de mercadería.",
  distribution_center: "Centro de distribución a otras sucursales.",
};

interface Location {
  id: string;
  name: string;
  address: string;
  phone: string | null;
  mapsUrl: string | null;
  hours: string | null;
  isDefault: boolean;
  active: boolean;
  sortOrder: number;
  createdAt: string;
  branchType?: BranchType;
  operationalWindowsEnabled?: boolean;
  operationalConfig?: unknown;
  latitude?: number | null;
  longitude?: number | null;
  placeId?: string | null;
}

const EMPTY_FORM = {
  name: "", phone: "", hours: "", isDefault: false, branchType: "store" as BranchType,
};

function mapsUrlFromGeo(lat: number, lon: number, placeId?: string | null): string {
  if (placeId?.trim()) {
    return `https://www.google.com/maps/search/?api=1&query=Google&query_place_id=${encodeURIComponent(placeId.trim())}`;
  }
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lon}`;
}

function addressFromLocation(loc: Location): NormalizedAddress {
  return {
    ...EMPTY_NORMALIZED_ADDRESS,
    formattedAddress: loc.address || "",
    latitude: loc.latitude ?? null,
    longitude: loc.longitude ?? null,
    placeId: loc.placeId ?? null,
  };
}

function SucursalesPage() {
  const { productFlags, businessProfile } = useTenantContext();
  const tid = useWorkspaceTenantId();
  const navigate = useNavigate();
  const goSettings = () => void navigate({ to: "/app/settings", search: {} as never });
  const canMultiLocation = productFlags.multi_location === true;
  const allowedBranchTypes = branchTypesForBusinessProfile(businessProfile);

  const [locs, setLocs]               = useState<Location[]>([]);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [dialogOpen, setDialogOpen]   = useState(false);
  const [editing, setEditing]         = useState<Location | null>(null);
  const [form, setForm]               = useState(EMPTY_FORM);
  const [geoAddress, setGeoAddress]   = useState<NormalizedAddress>({ ...EMPTY_NORMALIZED_ADDRESS });
  const [opsForm, setOpsForm]         = useState<LocationOpsFormState>(defaultLocationOpsForm());
  const [deleteTarget, setDeleteTarget] = useState<Location | null>(null);
  const [quotaModal, setQuotaModal] = useState<QuotaLimitPayload | null>(null);

  const load = () => {
    if (!tid) return;
    setLoading(true);
    apiGetJson<Location[]>("/api/locations", tid)
      .then(setLocs)
      .catch(() => toast.error("Error al cargar sucursales"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [tid]);

  const openCreate = () => {
    if (!canMultiLocation && locs.length >= 1) {
      toast.error("Para agregar más sucursales necesitás activar multi-sucursal (plan Pro o superior).");
      return;
    }
    setEditing(null);
    const defaultType = allowedBranchTypes.includes("store") ? "store" : allowedBranchTypes[0] ?? "store";
    setForm({ ...EMPTY_FORM, branchType: defaultType });
    setGeoAddress({ ...EMPTY_NORMALIZED_ADDRESS });
    setOpsForm(defaultLocationOpsForm());
    setDialogOpen(true);
  };

  const openEdit = (loc: Location) => {
    setEditing(loc);
    setForm({
      name:       loc.name,
      phone:      loc.phone ?? "",
      hours:      loc.hours ?? "",
      isDefault:  loc.isDefault,
      branchType: loc.branchType ?? "store",
    });
    setGeoAddress(addressFromLocation(loc));
    setOpsForm(locationOpsFromApi(loc));
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!tid) return;
    if (!form.name.trim()) { toast.error("El nombre es requerido"); return; }
    const addrErr = validateNormalizedAddress(geoAddress, { requireGeocoded: true });
    if (addrErr) { toast.error(addrErr); return; }
    const lat = Number(geoAddress.latitude);
    const lon = Number(geoAddress.longitude);
    const address =
      formatNormalizedAddressLine({
        ...geoAddress,
        postalCode: normalizeArPostalCode(geoAddress.postalCode),
      }) || geoAddress.formattedAddress.trim();
    setSaving(true);
    try {
      const payload = {
        name:       form.name.trim(),
        address,
        phone:      form.phone.trim() || null,
        mapsUrl:    mapsUrlFromGeo(lat, lon, geoAddress.placeId),
        hours:      form.hours.trim() || null,
        isDefault:  form.isDefault,
        branchType: form.branchType,
        operationalWindowsEnabled: opsForm.operationalWindowsEnabled,
        operationalConfig: opsForm.operationalWindowsEnabled ? opsForm.operationalConfig : null,
        latitude: lat,
        longitude: lon,
        placeId: geoAddress.placeId?.trim() || null,
      };
      if (editing) {
        await apiSendJson(`/api/locations?id=${editing.id}`, "PATCH", payload, tid);
        toast.success("Sucursal actualizada");
      } else {
        await apiSendJson("/api/locations", "POST", payload, tid);
        toast.success("Sucursal creada");
      }
      setDialogOpen(false);
      load();
    } catch (e: unknown) {
      const err = e as Error & { quota?: QuotaLimitPayload };
      if (err.quota) {
        setQuotaModal(err.quota);
        return;
      }
      toast.error(e instanceof Error ? e.message : "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (loc: Location) => {
    if (!tid) return;
    try {
      await apiSendJson(`/api/locations?id=${loc.id}`, "PATCH", { active: !loc.active }, tid);
      setLocs(prev => prev.map(l => l.id === loc.id ? { ...l, active: !l.active } : l));
    } catch {
      toast.error("Error al actualizar estado");
    }
  };

  const handleSetDefault = async (loc: Location) => {
    if (!tid || loc.isDefault) return;
    try {
      await apiSendJson(`/api/locations?id=${loc.id}`, "PATCH", { isDefault: true }, tid);
      setLocs(prev => prev.map(l => ({ ...l, isDefault: l.id === loc.id })));
      toast.success(`${loc.name} es ahora la sucursal principal`);
    } catch {
      toast.error("Error al actualizar");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || !tid) return;
    try {
      await apiDelete(`/api/locations?id=${deleteTarget.id}`, tid);
      toast.success("Sucursal eliminada");
      setLocs(prev => prev.filter(l => l.id !== deleteTarget.id));
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Error al eliminar");
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <div>
      <DashboardHeader layout="sticky" />

      <ClinicalPageShell className="mx-auto w-full max-w-[1400px] space-y-4">
      <SettingsSectionBack
        title="Sucursales"
        description="Gestioná dónde atendés a tus clientes."
        onBack={goSettings}
        actions={
          <Button
            onClick={openCreate}
            size="sm"
            className="gap-2"
            disabled={!canMultiLocation && locs.length >= 1}
            title={
              !canMultiLocation && locs.length >= 1
                ? "Requiere multi-sucursal (Pro+)"
                : undefined
            }
          >
            <Plus className="h-4 w-4" /> Nueva sucursal
          </Button>
        }
      />
      <CrudEditorShell
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title={editing ? "Editar sucursal" : "Nueva sucursal"}
        size="lg"
        list={
          loading ? (
            <PageLoader label="Cargando sucursales…" />
          ) : locs.length === 0 ? (
            <EmptyState onNew={openCreate} />
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {locs.map(loc => (
                <LocationCard
                  key={loc.id}
                  loc={loc}
                  onEdit={openEdit}
                  onDelete={setDeleteTarget}
                  onToggleActive={handleToggleActive}
                  onSetDefault={handleSetDefault}
                />
              ))}
            </div>
          )
        }
        footer={
          <>
            <Button variant="outline" className="flex-1" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button className="flex-1" onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editing ? "Guardar cambios" : "Crear sucursal"}
            </Button>
          </>
        }
      >
          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5">
              <FieldLabel htmlFor="loc-name" tipKey="branch.name" required>
                Nombre
              </FieldLabel>
              <Input
                id="loc-name"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Ej: Sucursal Centro, Depósito Norte"
                autoFocus
              />
            </div>
            <div className="grid gap-1.5">
              <Label>Tipo de sucursal</Label>
              <p className="text-[11px] text-muted-foreground">Define cómo opera esta sucursal. Permite preparar integraciones de stock, fulfillment y POS en el futuro.</p>
              <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                {(Object.entries(BRANCH_TYPE_LABELS) as [BranchType, string][])
                  .filter(([type]) => (allowedBranchTypes as readonly string[]).includes(type))
                  .map(([type, label]) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, branchType: type }))}
                    className={[
                      "flex flex-col items-start gap-0.5 rounded-md border p-2 text-left text-xs transition-all",
                      form.branchType === type
                        ? "border-primary bg-primary/5 font-medium"
                        : "border-border text-muted-foreground hover:border-muted-foreground/40",
                    ].join(" ")}
                  >
                    <span className="font-medium leading-tight">{label}</span>
                    <span className="text-[10px] leading-tight opacity-70">{BRANCH_TYPE_DESCRIPTIONS[type]}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <FieldLabel tipKey="branch.address">Dirección (Google Maps)</FieldLabel>
              <p className="text-[11px] text-muted-foreground">
                Obligatorio elegir la calle desde las sugerencias para guardar lat/lng y calcular el radio de delivery.
              </p>
              <NormalizedAddressForm
                value={geoAddress}
                onChange={setGeoAddress}
                variant="default"
                showLabel={false}
                showNotes={false}
                requirePlacesSelection
              />
              {geoAddress.latitude != null && geoAddress.longitude != null ? (
                <p className="text-[11px] text-emerald-500">
                  Ubicación OK · {Number(geoAddress.latitude).toFixed(5)}, {Number(geoAddress.longitude).toFixed(5)}
                </p>
              ) : (
                <p className="text-[11px] text-amber-500">Todavía sin coordenadas — buscá y elegí una sugerencia.</p>
              )}
            </div>
            <div className="grid gap-1.5">
              <FieldLabel htmlFor="loc-phone" tipKey="branch.phone">Teléfono</FieldLabel>
              <Input
                id="loc-phone"
                value={form.phone}
                onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                placeholder="+54 11 1234-5678"
              />
            </div>
            <div className="grid gap-1.5">
              <FieldLabel htmlFor="loc-hours" tipKey="branch.hours">Horario</FieldLabel>
              <Input
                id="loc-hours"
                value={form.hours}
                onChange={e => setForm(f => ({ ...f, hours: e.target.value }))}
                placeholder="Lun–Vie 9–18 hs, Sáb 9–13 hs"
              />
            </div>
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <p className="text-sm font-medium">Sucursal principal</p>
                <p className="text-xs text-muted-foreground">Aparece primero en el catálogo</p>
              </div>
              <Switch
                checked={form.isDefault}
                onCheckedChange={v => setForm(f => ({ ...f, isDefault: v }))}
              />
            </div>
            <LocationOperationalWindowsForm state={opsForm} onChange={setOpsForm} />
          </div>
      </CrudEditorShell>
      </ClinicalPageShell>

      {/* Delete Confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={open => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar sucursal?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará <strong>{deleteTarget?.name}</strong>.
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <QuotaLimitModal open={quotaModal != null} payload={quotaModal} onClose={() => setQuotaModal(null)} />
    </div>
  );
}

function LocationCard({
  loc,
  onEdit,
  onDelete,
  onToggleActive,
  onSetDefault,
}: {
  loc: Location;
  onEdit: (l: Location) => void;
  onDelete: (l: Location) => void;
  onToggleActive: (l: Location) => void;
  onSetDefault: (l: Location) => void;
}) {
  return (
    <div className={`rounded-lg border bg-card p-4 flex flex-col gap-3 ${!loc.active ? "opacity-60" : ""}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <MapPin className="h-4 w-4 shrink-0 text-green-600" />
          <span className="font-semibold truncate">{loc.name}</span>
          {loc.isDefault && (
            <Badge variant="secondary" className="gap-1 shrink-0 text-xs">
              <Star className="h-3 w-3" /> Principal
            </Badge>
          )}
        </div>
        <div className="flex gap-1 shrink-0">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(loc)}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-destructive hover:text-destructive"
            onClick={() => onDelete(loc)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Info */}
      <div className="text-sm text-muted-foreground space-y-1">
        <p className="truncate">{loc.address}</p>
        {loc.latitude != null && loc.longitude != null ? (
          <p className="text-[11px] text-emerald-600 dark:text-emerald-400">Mapeada · lista para radio de envío</p>
        ) : (
          <p className="text-[11px] text-amber-600 dark:text-amber-400">Sin mapa — editá y elegí la dirección en Google</p>
        )}
        {loc.phone && (
          <p className="flex items-center gap-1">
            <Phone className="h-3 w-3" /> {loc.phone}
          </p>
        )}
        {loc.hours && <p className="text-xs">{loc.hours}</p>}
        {loc.operationalWindowsEnabled && (
          <p className="flex items-center gap-1 text-xs text-green-700 dark:text-green-400">
            <Clock className="h-3 w-3" /> Horarios operativos activos
          </p>
        )}
        {loc.mapsUrl && (
          <a
            href={loc.mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-green-600 hover:underline"
          >
            Ver en Maps
          </a>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-1 border-t">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Switch
            checked={loc.active}
            onCheckedChange={() => onToggleActive(loc)}
            className="scale-75"
          />
          <span>{loc.active ? "Activa" : "Inactiva"}</span>
        </div>
        {!loc.isDefault && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-xs text-muted-foreground"
            onClick={() => onSetDefault(loc)}
          >
            Hacer principal
          </Button>
        )}
      </div>
    </div>
  );
}

function EmptyState({ onNew }: { onNew: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border px-4 py-8 text-center">
      <MapPin className="h-6 w-6 text-muted-foreground/60" />
      <div className="space-y-1">
        <p className="text-sm font-medium">Todavía no tenés sucursales</p>
        <p className="max-w-sm text-xs text-muted-foreground">
          Agregá tu primer punto de venta para indicar dónde pueden encontrarte.
        </p>
      </div>
      <Button onClick={onNew} size="sm" className="gap-2">
        <Plus className="h-4 w-4" /> Agregar primera sucursal
      </Button>
    </div>
  );
}
